import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowUpRight, ArrowRight } from "lucide-react";
import { IMG } from "@/lib/brand";
import { Reveal, SectionEyebrow, SectionHeader, HexIcon } from "@/components/Section";
import { HexMark, HexWatermark } from "@/components/BrandMark";
import { useLang } from "@/lib/LanguageContext";

const PRODUCT_IMAGES = [IMG.pellets, IMG.texture1, IMG.wood, IMG.polymers1, IMG.polymers2, IMG.printing];
const INDUSTRY_IMAGES = [IMG.industrial1, IMG.texture2, IMG.industrial2, IMG.wood];

export default function Home() {
  const { t } = useLang();
  const h = t.home;

  return (
    <div data-testid="home-page">
      {/* HERO */}
      <section data-testid="home-hero" className="relative min-h-[92vh] flex items-end overflow-hidden bg-primary">
        <img src={IMG.hero} alt="Industrial polymer compounding" className="absolute inset-0 w-full h-full object-cover opacity-55" />
        <div className="absolute inset-0 bg-gradient-to-t from-primary via-primary/55 to-primary/15" />
        <div className="absolute inset-0 hex-pattern-light opacity-50" />

        <div className="relative container-x pb-20 sm:pb-28 pt-32 w-full">
          <motion.div
            initial={{ opacity: 0, y: 24 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.9, ease: [0.22, 1, 0.36, 1] }}
            className="max-w-5xl"
          >
            <div className="overline text-white/70">{h.hero_eyebrow}</div>
            <h1 className="mt-7 display-xxl text-white text-[clamp(2.6rem,7vw,6.25rem)]">
              {h.hero_l1}
              <br />
              {h.hero_l2}
              <br />
              <span className="text-accent">{h.hero_l3_a}</span>
            </h1>
            <p className="mt-10 max-w-2xl text-white/80 text-lg sm:text-xl leading-relaxed">{h.hero_intro}</p>

            <div className="mt-12 flex flex-wrap items-center gap-5">
              <Link
                to="/products"
                data-testid="hero-cta-products"
                className="group inline-flex items-center gap-3 bg-white text-primary px-7 py-4 text-sm font-medium tracking-wide hover:bg-accent hover:text-white transition-all"
              >
                {h.hero_cta1}
                <ArrowUpRight className="w-4 h-4 transition-transform group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
              </Link>
              <Link
                to="/contact"
                data-testid="hero-cta-contact"
                className="group inline-flex items-center gap-3 border border-white/40 text-white px-7 py-4 text-sm font-medium tracking-wide hover:bg-white/10 transition-all"
              >
                {h.hero_cta2}
                <ArrowRight className="w-4 h-4 transition-transform group-hover:translate-x-1" />
              </Link>
            </div>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 18 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.9, delay: 0.25, ease: [0.22, 1, 0.36, 1] }}
            className="mt-20 grid grid-cols-2 md:grid-cols-4 gap-px bg-white/15 border-y border-white/15"
          >
            {h.metrics.map(([n, l]) => (
              <div key={l} className="bg-primary/70 backdrop-blur-md py-7 px-6">
                <div className="font-display text-3xl sm:text-4xl tick text-white">{n}</div>
                <div className="mt-1 text-[11px] tracking-[0.22em] uppercase text-white/60">{l}</div>
              </div>
            ))}
          </motion.div>
        </div>
      </section>

      {/* PRODUCTS */}
      <section data-testid="home-products" className="relative py-24 sm:py-32 bg-white container-x overflow-hidden">
        <HexWatermark
          opacity={0.04}
          size={640}
          strokeWidth={1.2}
          className="text-primary -right-40 -top-32"
        />
        <div className="flex flex-col lg:flex-row lg:items-end lg:justify-between gap-8">
          <SectionHeader
            eyebrow={h.products_eyebrow}
            title={h.products_title}
            intro={h.products_intro}
            testId="home-products-header"
          />
          <Link to="/products" className="inline-flex items-center gap-2 text-primary text-sm font-medium link-underline" data-testid="home-products-all-link">
            {h.products_all} <ArrowRight className="w-4 h-4" />
          </Link>
        </div>

        <div className="mt-16 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-px bg-border">
          {h.products_grid.map((name, idx) => (
            <Reveal key={name} delay={idx * 0.04}>
              <Link to="/products" data-testid={`home-product-card-${idx}`} className="group block bg-white p-6 hover:bg-secondary transition-colors">
                <div className="img-zoom aspect-[4/3] bg-secondary mb-6">
                  <img src={PRODUCT_IMAGES[idx]} alt={name} className="w-full h-full object-cover" />
                </div>
                <div className="flex items-start justify-between gap-3">
                  <div>
                    <div className="text-[11px] tracking-[0.2em] uppercase text-muted-foreground">{String(idx + 1).padStart(2, "0")}</div>
                    <h3 className="mt-2 font-display text-xl tracking-tight font-medium">{name}</h3>
                  </div>
                  <ArrowUpRight className="w-5 h-5 text-muted-foreground transition-all group-hover:text-accent group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
                </div>
              </Link>
            </Reveal>
          ))}
        </div>
      </section>

      {/* SERVICES */}
      <section data-testid="home-services" className="bg-secondary border-y border-border">
        <div className="container-x py-24 sm:py-32 grid lg:grid-cols-12 gap-12 lg:gap-20">
          <div className="lg:col-span-5">
            <SectionEyebrow>{h.services_eyebrow}</SectionEyebrow>
            <h2 className="mt-5 font-display text-4xl sm:text-5xl tracking-tight font-medium leading-[1.05]">{h.services_title}</h2>
            <p className="mt-6 text-muted-foreground leading-relaxed text-lg">{h.services_intro}</p>
            <Link to="/services" data-testid="home-services-link" className="mt-10 inline-flex items-center gap-2 text-primary text-sm font-medium link-underline">
              {h.services_link} <ArrowRight className="w-4 h-4" />
            </Link>
          </div>
          <div className="lg:col-span-7">
            <ul className="divide-y divide-border border-y border-border">
              {h.services_list.map((s, i) => (
                <li key={s}>
                  <Link to="/services" data-testid={`home-service-row-${i}`} className="group flex items-center justify-between py-6 hover:px-2 transition-all">
                    <div className="flex items-baseline gap-6">
                      <span className="text-xs tabular-nums text-muted-foreground tracking-wider">0{i + 1}</span>
                      <span className="font-display text-2xl sm:text-3xl tracking-tight">{s}</span>
                    </div>
                    <ArrowUpRight className="w-5 h-5 text-muted-foreground transition-all group-hover:text-accent" />
                  </Link>
                </li>
              ))}
            </ul>
          </div>
        </div>
      </section>

      {/* INDUSTRIES */}
      <section data-testid="home-industries" className="container-x py-24 sm:py-32 bg-white">
        <SectionHeader
          eyebrow={h.industries_eyebrow}
          title={h.industries_title}
          intro={h.industries_intro}
          testId="home-industries-header"
        />
        <div className="mt-16 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-px bg-border">
          {h.industries_grid.map((name, idx) => (
            <Reveal key={name} delay={idx * 0.05}>
              <Link to="/industries" data-testid={`home-industry-${idx}`} className="group relative block bg-primary aspect-[4/5] overflow-hidden">
                <img src={INDUSTRY_IMAGES[idx]} alt={name} className="absolute inset-0 w-full h-full object-cover opacity-65 group-hover:opacity-80 group-hover:scale-105 transition-all duration-700" />
                <div className="absolute inset-0 bg-gradient-to-t from-primary via-primary/40 to-transparent" />
                <div className="relative h-full p-7 flex flex-col justify-between">
                  <span className="text-[11px] tracking-[0.22em] uppercase text-white/60">{h.industries_industry} · 0{idx + 1}</span>
                  <div>
                    <h3 className="font-display text-2xl text-white tracking-tight">{name}</h3>
                    <div className="mt-3 inline-flex items-center gap-2 text-white text-sm">
                      {h.industries_explore} <ArrowRight className="w-4 h-4" />
                    </div>
                  </div>
                </div>
              </Link>
            </Reveal>
          ))}
        </div>
      </section>

      {/* INNOVATION */}
      <section data-testid="home-innovation" className="bg-secondary border-y border-border">
        <div className="container-x py-24 sm:py-32 grid lg:grid-cols-12 gap-12 items-center">
          <Reveal>
            <div className="lg:col-span-12 grid lg:grid-cols-12 gap-12 items-center">
              <div className="lg:col-span-6 img-zoom aspect-[4/3] border border-border">
                <img src={IMG.lab1} alt="Material laboratory" className="w-full h-full object-cover" />
              </div>
              <div className="lg:col-span-6">
                <SectionEyebrow>{h.innovation_eyebrow}</SectionEyebrow>
                <h2 className="mt-5 font-display text-4xl sm:text-5xl tracking-tight font-medium leading-[1.05]">
                  {h.innovation_title_l1}
                  <br /> {h.innovation_title_l2}
                </h2>
                <p className="mt-6 text-muted-foreground text-lg leading-relaxed">{h.innovation_intro}</p>
                <div className="mt-10 grid grid-cols-2 gap-px bg-border border border-border">
                  {h.innovation_labs.map(([code, label]) => (
                    <div key={code} className="bg-secondary p-5">
                      <div className="font-display text-2xl tracking-tight">{code}</div>
                      <div className="mt-1 text-xs tracking-[0.15em] uppercase text-muted-foreground">{label}</div>
                    </div>
                  ))}
                </div>
                <Link to="/innovation" data-testid="home-innovation-link" className="mt-10 inline-flex items-center gap-2 text-primary text-sm font-medium link-underline">
                  {h.innovation_link} <ArrowRight className="w-4 h-4" />
                </Link>
              </div>
            </div>
          </Reveal>
        </div>
      </section>

      {/* WHY BALTIVEX */}
      <section data-testid="home-why" className="relative container-x py-24 sm:py-32 bg-white overflow-hidden">
        <HexWatermark
          opacity={0.045}
          size={680}
          strokeWidth={1.2}
          className="text-primary -left-48 top-1/2 -translate-y-1/2"
        />
        <div className="grid lg:grid-cols-12 gap-12">
          <div className="lg:col-span-4">
            <SectionEyebrow>{h.why_eyebrow}</SectionEyebrow>
            <h2 className="mt-5 font-display text-4xl sm:text-5xl tracking-tight font-medium leading-[1.05]">{h.why_title}</h2>
          </div>
          <div className="lg:col-span-8 grid sm:grid-cols-2 gap-px bg-border border border-border">
            {h.why_cards.map((f, i) => (
              <div key={f.t} data-testid={`home-why-card-${i}`} className="bg-white p-8 sm:p-10">
                <HexIcon className="text-accent">
                  <span className="text-accent font-display font-medium">0{i + 1}</span>
                </HexIcon>
                <h3 className="mt-6 font-display text-xl font-medium tracking-tight">{f.t}</h3>
                <p className="mt-3 text-muted-foreground leading-relaxed">{f.d}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section data-testid="home-cta" className="relative bg-primary overflow-hidden">
        <div className="absolute inset-0 hex-pattern-light opacity-40" />
        <HexWatermark
          opacity={0.08}
          size={820}
          strokeWidth={1.5}
          className="text-white -right-60 top-1/2 -translate-y-1/2"
        />
        <div className="relative container-x py-24 sm:py-32 grid lg:grid-cols-12 gap-12 items-end">
          <div className="lg:col-span-8">
            <SectionEyebrow light>{h.cta_eyebrow}</SectionEyebrow>
            <h2 className="mt-5 font-display text-white text-4xl sm:text-6xl tracking-tight font-medium leading-[1.05]">
              {h.cta_title_l1}
              <br /> {h.cta_title_l2} <span className="text-accent">{h.cta_title_l3}</span>
            </h2>
          </div>
          <div className="lg:col-span-4 flex lg:justify-end">
            <Link
              to="/contact"
              data-testid="home-cta-button"
              className="group inline-flex items-center gap-3 bg-white text-primary px-8 py-4 text-sm font-medium tracking-wide hover:bg-accent hover:text-white transition-all"
            >
              {h.cta_button}
              <ArrowUpRight className="w-4 h-4 transition-transform group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
}
